import { useFeature } from '@optimizely/react-sdk';
import { VariableValuesObject } from '@optimizely/react-sdk/dist/client';
import { useLocation } from 'react-router-dom';
import { paramsFromSearchString } from 'Shared/utils';

/*
  This hook functions as a wrapper to Optimizely's built in React useFeature hook
  Only useful in conjunction with the QA Toggle Audience on Optimizely
  Intended use:
  - Create new feature on Optimizely
  - While feature is not live for Production users:
    - Turn feature on 100% in Development Environment
    - Set audience to QA Toggle
    - query param of qa_toggle=off will turn the feature flag off
    - no qa_toggle query param or qa_toggle=on will turn the feature flag on
  - When feature is ready for production:
    - Turn feature on 100% in Production Environment
    - Remove QA Toggle Audience (return to default of "Everyone")
  - Cleanup:
    - Remove usage of this hook for the feature in the code
    - Archive the feature flag in Optimizely
*/

// Types defined from https://docs.developers.optimizely.com/full-stack/docs/hooks-react
type HookOptions = {
  autoUpdate?: boolean;
  timeout?: number;
};

type HookOverrides = {
  overrideUserId?: string;
  overrideAttributes?: Record<string, any>;
};

export const useQaToggleFeature = (
  feature: string,
  options?: HookOptions,
  overrides?: HookOverrides
): [boolean, VariableValuesObject, boolean, boolean] => {
  const location = useLocation();
  const params = paramsFromSearchString(location.search);
  const [isFeatureEnabled, variables, clientReady, didTimeout] = useFeature(
    feature,
    options || {},
    {
      ...overrides,
      overrideAttributes: params.qa_toggle
        ? {
            ...overrides?.overrideAttributes,
            qa_toggle: params.qa_toggle,
          }
        : { ...overrides?.overrideAttributes },
    }
  );

  return [isFeatureEnabled, variables, clientReady, didTimeout];
};
